import { ColumnType } from '@dynamika-shared/ui/Table/Table.types';
import { StatusPill } from '@dynamika-shared/ui/StatusPill/StatusPill';
import { Dropdown } from '@dynamika-shared/ui/Dropdown/Dropdown';
import { DotsIcon, SuspendIcon } from '@dynamika-shared/ui/icons';
import { TruncatedText } from '@dynamika-shared/ui/TruncatedText/TruncatedText';
import { format } from 'date-fns';
import { InvitationStatusEnum } from '@dynamika-shared/types/status';

import type { Invitation } from '@restore/invitationsUserManagement/invitationsUserManagement.types';

export enum InvitationActionType {
  revoke = 'revoke',
}

export type MakeInvitationsColumnsType = {
  onInvitationClick: (
    invitation: Invitation,
    action: InvitationActionType
  ) => void;
  facilityMap: Record<string, string>;
};

export function makeInvitationsColumns({
  onInvitationClick,
  facilityMap,
}: MakeInvitationsColumnsType): ColumnType<Invitation>[] {
  return [
    {
      name: 'invitedEmail',
      header() {
        return 'Email';
      },
      loading: index => {
        const width = Number(index) % 2 ? 'w-[20rem]' : 'w-[17rem]';
        return (
          <div
            className={`h-2 ${width} animate-pulse rounded-[5px] bg-brightGray`}
          />
        );
      },
      getValue: row => (
        <TruncatedText
          text={row.invitedEmail}
          truncate
          tooltip
        />
      ),
      thClassName: 'w-[20%]',
    },
    {
      name: 'role',
      header() {
        return 'Role';
      },
      loading: () => (
        <div
          className={'h-2 w-[10rem] animate-pulse rounded-[5px] bg-brightGray'}
        />
      ),
      getValue: row => row.role,
      thClassName: 'w-[15%]',
    },
    {
      name: 'status',
      header() {
        return 'Status';
      },
      loading: index => {
        const width = Number(index) % 2 ? 'w-[13rem]' : 'w-[10rem]';
        return (
          <div
            className={`h-2 ${width} animate-pulse rounded-md bg-brightGray`}
          />
        );
      },
      getValue(row) {
        return <StatusPill status={row.status} />;
      },
      thClassName: 'w-[12%]',
    },
    {
      name: 'facilityId',
      header() {
        return 'Facility';
      },
      loading: () => (
        <div
          className={'h-2 w-[12rem] animate-pulse rounded-[5px] bg-brightGray'}
        />
      ),
      getValue: row => facilityMap[row.facilityId] ?? row.facilityId ?? '-',
      thClassName: 'w-[15%]',
    },
    {
      name: 'invitedBy',
      header() {
        return 'Invited By';
      },
      loading: () => (
        <div
          className={'h-2 w-[10rem] animate-pulse rounded-[5px] bg-brightGray'}
        />
      ),
      getValue: row => (
        <TruncatedText
          text={row.invitedByFullName ?? '-'}
          truncate
          tooltip
        />
      ),
      thClassName: 'w-[13%]',
    },
    {
      name: 'created',
      header() {
        return 'Created';
      },
      loading: () => (
        <div
          className={'h-2 w-[10rem] animate-pulse rounded-[5px] bg-brightGray'}
        />
      ),
      getValue(row) {
        return (
          <div>
            {row.created ? format(new Date(row.created), 'dd-LLL-yyyy') : '-'}
          </div>
        );
      },
      thClassName: 'w-[10%]',
    },
    {
      name: 'expiresAt',
      header() {
        return 'Expires At';
      },
      loading: () => (
        <div
          className={'h-2 w-[10rem] animate-pulse rounded-[5px] bg-brightGray'}
        />
      ),
      getValue(row) {
        return (
          <div>
            {row.expiresAt
              ? format(new Date(row.expiresAt), 'dd-LLL-yyyy')
              : '-'}
          </div>
        );
      },
      thClassName: 'w-[10%]',
    },
    {
      name: 'actions',
      header() {
        return 'Action';
      },
      loading: () => (
        <div className='h-3 w-3 animate-pulse rounded-md bg-brightGray' />
      ),
      getValue(invitation) {
        if (invitation.status !== InvitationStatusEnum.PENDING) {
          return null;
        }
        return (
          <div data-testid={`invitationAction-${invitation.id}`}>
            <div className='ml-1 flex items-center justify-center'>
              <Dropdown
                items={[
                  {
                    key: InvitationActionType.revoke,
                    label: 'Revoke',
                    icon: <SuspendIcon />,
                  },
                ]}
                overlayClassName='w-[20rem] [&.antd-custom-dropdown_.ant-dropdown-menu]:!pt-0'
                trigger={['click']}
                onClick={e =>
                  onInvitationClick(
                    invitation,
                    e.key as unknown as InvitationActionType
                  )
                }
              >
                <div className='flex h-5 cursor-pointer items-center justify-center'>
                  <div className='rounded-3xl p-1 hover:bg-waterLight'>
                    <DotsIcon className='hover:text-black' />
                  </div>
                </div>
              </Dropdown>
            </div>
          </div>
        );
      },
      alignToRight: true,
    },
  ];
}
